<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { Select, Input, Checkbox, SelectOption } from 'ant-design-vue'
  import { AnalysisConfigOption } from '../models/AnalysisConfigOption'

  const props = defineProps({
    modelValue: { type: null, default: undefined }, // value for Select mode
    defaultModelValue: { type: null, default: undefined },
    alternativeModelValue: { type: null, default: undefined }, // value for Input mode
    inactiveFieldDefaultValue: { type: null, default: undefined },
    alternativeOptions: { type: Array<AnalysisConfigOption>, default: () => [] },
    placeholder: { type: String, default: '' },
    allowClear: { type: Boolean, default: true },
    toggleLabel: { type: String, default: 'Use custom value' }
  })

  const emit = defineEmits(['update:modelValue', 'update:alternativeModelValue'])

  const computeToggleState = (): boolean => {
    const m = props.modelValue
    const a = props.alternativeModelValue
    const inactive = props.inactiveFieldDefaultValue
    if (m === inactive && a !== inactive) return true
    if (a === inactive && m !== inactive) return false
    return false
  }

  // Local reactive mirrors of incoming props. These are used for v-model binding in the template.
  // Changes to these locals are emitted back to the parent.
  const isToggleEnabled = ref<boolean>(computeToggleState())
  const localInputValue = ref<any>(props.modelValue || props.inactiveFieldDefaultValue)
  const localSelectValue = ref<any>(props.alternativeModelValue ?? props.inactiveFieldDefaultValue)

  // Keep locals in sync when props change
  watch(
    () => props.modelValue,
    (v) => (localInputValue.value = v),
    { immediate: true }
  )
  watch(
    () => props.alternativeModelValue,
    (v) => (localSelectValue.value = v),
    { immediate: true }
  )

  // Emit updates when locals change so parent can bind with v-model:*
  watch(localInputValue, (v) => emit('update:modelValue', v))
  watch(localSelectValue, (v) => emit('update:alternativeModelValue', v))

  // When toggle changes we emit update for the toggle prop as well (if parent bound it)
  const onToggleChange = (checked: boolean) => {
    isToggleEnabled.value = checked

    if (checked) {
      localInputValue.value = props.inactiveFieldDefaultValue
      localSelectValue.value = props.alternativeOptions?.length
        ? props.alternativeOptions[0].value
        : undefined
    } else {
      localInputValue.value = props.defaultModelValue
      localSelectValue.value = props.inactiveFieldDefaultValue
    }
  }
</script>

<template>
  <div>
    <Checkbox :checked="isToggleEnabled" @change="(e: any) => onToggleChange(e.target.checked)">
      {{ toggleLabel }}
    </Checkbox>

    <div class="mt-2">
      <!-- Select mode -->
      <Select
        v-if="isToggleEnabled"
        :value="localSelectValue"
        @change="(v: any) => (localSelectValue = v)"
        :allow-clear="allowClear"
        showSearch
        size="large"
        placeholder="Select..."
        class="w-full"
      >
        <SelectOption
          v-for="option in alternativeOptions"
          :key="option.value"
          :value="option.value"
        >
          <span v-html="option.label"></span>
        </SelectOption>
      </Select>

      <Input
        v-else
        :value="localInputValue"
        @update:value="(v: any) => (localInputValue = v)"
        :placeholder="placeholder"
        size="large"
        class="w-full"
        allowClear
      />
    </div>
  </div>
</template>

<style scoped></style>
